import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { Search, Wrench, Car, User, Clock, TriangleAlert, ShoppingCart, ListFilter, Check, X } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { VehicleService, VehicleServiceStatus } from '../../../../shared/types/vehicle-service.types';
import { SettingListItem } from '../../../../shared/types/setting.types';
import { Vehicle } from '../../../../shared/types/vehicle.types';
import useSettingLists from '../../wrappers/use-setting-list-item';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleService from '../../wrappers/use-vehicle-service';
import VehiclePicture from '../../components/VehiclePicture';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { serviceQueueStyles } from './ServiceQueue.styles';
import { useAuth } from '../../contexts/AuthContext';

const statusConfig: Record<string, { color: 'default' | 'primary' | 'info' | 'warning' | 'success' | 'error'; icon: React.ReactElement }> = {
    [VehicleServiceStatus.Ordered]: { color: 'primary', icon: <ShoppingCart size={12} /> },
    [VehicleServiceStatus.InProgress]: { color: 'info', icon: <Wrench size={12} /> },
    [VehicleServiceStatus.WaitingForParts]: { color: 'warning', icon: <TriangleAlert size={12} /> },
    [VehicleServiceStatus.Completed]: { color: 'success', icon: <Check size={12} /> },
    [VehicleServiceStatus.Cancelled]: { color: 'default', icon: <X size={12} /> },
};

interface EnrichedService extends VehicleService {
    vehicleModel?: string;
    customerName?: string;
    vehicle?: Vehicle;
}

const ServiceQueue: React.FC = () => {
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { user } = useAuth();
    const settingLists = useSettingLists();
    const { get: getVehicle } = useVehicle();
    const { list: listServices } = useVehicleService();
    const [services, setServices] = useState<EnrichedService[]>([]);
    const [loading, setLoading] = useState(true);
    const [serviceTypeMap, setServiceTypeMap] = useState<Record<string, string>>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        settingLists.list('serviceTypes').then((items) => {
            setServiceTypeMap(Object.fromEntries(items.map((i: SettingListItem) => [i.id, i.label])));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!user?.dealerId) {
            setServices([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setServices([]);

        const fetchServices = async () => {
            try {
                const rawServices: VehicleService[] = await listServices({ dealerId: user.dealerId });

                const uniqueVins = [...new Set(rawServices.map((s) => s.vehicleUid))];
                const vehicleMap: Record<string, Vehicle> = {};
                await Promise.all(
                    uniqueVins.map(async (vin) => {
                        const vehicle = await getVehicle(vin);
                        if (vehicle) vehicleMap[vin] = vehicle;
                    })
                );

                const uniqueUserIds = [...new Set(rawServices.map((s) => s.createdBy))];
                const userNameMap: Record<string, string> = {};
                await Promise.all(
                    uniqueUserIds.map(async (uid) => {
                        const snap = await getDoc(doc(firestore, 'user', uid));
                        if (snap.exists()) userNameMap[uid] = snap.data().name ?? '';
                    })
                );

                setServices(rawServices.map((s) => ({
                    ...s,
                    vehicleModel: vehicleMap[s.vehicleUid]?.model,
                    customerName: userNameMap[s.createdBy],
                    vehicle: vehicleMap[s.vehicleUid],
                })));
            } finally {
                setLoading(false);
            }
        };

        fetchServices();
    }, [getVehicle, listServices, user?.dealerId]);

    const filteredServices = services.filter((s) => {
        const lower = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm ||
            s.vehicleUid.toLowerCase().includes(lower) ||
            (s.vehicleModel ?? '').toLowerCase().includes(lower) ||
            (s.customerName ?? '').toLowerCase().includes(lower);
        const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    return (
        <Box sx={serviceQueueStyles.root}>
            <Box sx={serviceQueueStyles.header}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>{t('serviceQueue.title')}</Typography>
                    <Typography color="text.secondary" mt={0.5}>{t('serviceQueue.subtitle')}</Typography>
                </Box>
                <Button variant="contained" startIcon={<Wrench size={20} />} component={Link} to="/app/service-desk">
                    {t('serviceQueue.newIntake')}
                </Button>
            </Box>

            {!user?.dealerId && <Alert severity="warning">{t('serviceQueue.missingDealerId')}</Alert>}

            <Paper elevation={0} sx={serviceQueueStyles.filterBar}>
                <TextField
                    size="small"
                    placeholder={t('serviceQueue.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search size={20} /></InputAdornment> }}
                    sx={serviceQueueStyles.searchField}
                />
                <FormControl size="small" sx={serviceQueueStyles.statusControl}>
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        startAdornment={<InputAdornment position="start"><ListFilter size={20} /></InputAdornment>}
                    >
                        <MenuItem value="all">{t('serviceQueue.filterAll')}</MenuItem>
                        <MenuItem value={VehicleServiceStatus.Ordered}>{t('vehicleServiceStatus.ordered')}</MenuItem>
                        <MenuItem value={VehicleServiceStatus.InProgress}>{t('vehicleServiceStatus.inProgress')}</MenuItem>
                        <MenuItem value={VehicleServiceStatus.WaitingForParts}>{t('vehicleServiceStatus.waitingForParts')}</MenuItem>
                        <MenuItem value={VehicleServiceStatus.Completed}>{t('vehicleServiceStatus.completed')}</MenuItem>
                        <MenuItem value={VehicleServiceStatus.Cancelled}>{t('vehicleServiceStatus.cancelled')}</MenuItem>
                    </Select>
                </FormControl>
            </Paper>

            {loading ? (
                <Box sx={serviceQueueStyles.loading}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={serviceQueueStyles.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow sx={serviceQueueStyles.tableHeaderRow}>
                                <TableCell sx={serviceQueueStyles.tableHeadCell}>{t('serviceQueue.colMachine')}</TableCell>
                                <TableCell sx={serviceQueueStyles.tableHeadCell}>{t('serviceQueue.colCustomer')}</TableCell>
                                <TableCell sx={serviceQueueStyles.tableHeadCell}>{t('serviceQueue.colServiceType')}</TableCell>
                                <TableCell sx={serviceQueueStyles.tableHeadCell}>{t('serviceQueue.colDates')}</TableCell>
                                <TableCell sx={serviceQueueStyles.tableHeadCell}>{t('serviceQueue.colStatus')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredServices.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={serviceQueueStyles.emptyRowCell}>
                                        {t('serviceQueue.empty')}
                                    </TableCell>
                                </TableRow>
                            ) : filteredServices.map((service) => {
                                const status = statusConfig[service.status];
                                return (
                                    <TableRow
                                        key={service.id}
                                        hover
                                        component={Link}
                                        to={`/app/service-desk/services/${service.id}`}
                                        sx={serviceQueueStyles.clickableRow}
                                    >
                                        <TableCell>
                                            <Box sx={serviceQueueStyles.machineCellContent}>
                                                <Box sx={serviceQueueStyles.thumbnailFrame}>
                                                    {service.vehicle
                                                        ? <VehiclePicture vehicle={service.vehicle} imgSx={{ height: 48, objectFit: 'cover' }} />
                                                        : <Box sx={serviceQueueStyles.thumbnailFallback}><Car size={24} color="#757575" /></Box>
                                                    }
                                                </Box>
                                                <Box>
                                                    {service.vehicleModel && (
                                                        <Typography variant="body2" fontWeight={600}>{service.vehicleModel}</Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">{service.vehicleUid}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={serviceQueueStyles.customerCellContent}>
                                                <User size={16} color="#757575" />
                                                <Typography variant="body2">{service.customerName ?? '—'}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{serviceTypeMap[service.serviceType] ?? service.serviceType}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary" display="block">{t('serviceQueue.received')}</Typography>
                                            <Typography variant="body2">{formatDate(service.createdAt)}</Typography>
                                            {service.plannedFinish && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={serviceQueueStyles.plannedFinishLabel}>
                                                        <Clock size={10} />{t('serviceQueue.plannedFinish')}
                                                    </Typography>
                                                    <Typography variant="body2">{formatDate(service.plannedFinish)}</Typography>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t(`vehicleServiceStatus.${service.status}`, { defaultValue: service.status })}
                                                color={status?.color ?? 'default'}
                                                size="small"
                                                icon={status?.icon}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default ServiceQueue;
